import React from 'react';
import { connect } from 'react-redux';
import { cloneDeep, get, isEmpty, isEqual, isPlainObject, omit } from 'lodash';
import { Accordion, AccordionDetails, AccordionSummary, Button, Tooltip } from '@mui/material';
import cx from 'classnames';
import { ReactComponent as ArrowDropDown } from 'src/new/components/icons/chevron_down.svg';
import { ReactComponent as ArrowRight } from 'src/new/components/icons/chevron_right.svg';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import JSONUtils from 'js/lib/JSONUtils';
import UIUtils from 'js/lib/UIUtils';
import { applyMacroReplacement } from 'js/lib/MacroReplacementUtils';
import styles from './TestTab.module.scss';
import CircularLoader from 'js/components/elements/progress/CircularLoader';
import { ReactComponent as InfoIcon } from 'src/new/components/icons/fi-rr-info.svg';
import { ReactComponent as CloseFilledIcon } from 'new/components/icons/CloseFilled.svg';
import { ReactComponent as CheckFilledIcon } from 'new/components/icons/check-filled.svg';
import { testDataset } from 'js/actions/probe';
import { accumulateSchema } from 'js/actions/dataSets';
import {
  accumulateSchemaToDestinationDraft,
  updateDestinationDraft,
} from 'js/actions/recipeBuilder';
import { setFormError as setFormErrorConnect } from 'js/actions';
import { FORM_TYPE_EDIT_DESTINATION } from 'js/constants/formTypes';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import usePaging from 'new/state/paging/repository/usePaging';
import { ResizableVerticalLayout } from 'js/components/elements/ResizableVerticalLayout';

const RESIZABLE_VERTICAL_LAYOUT_MIN_SIZES = [90, 600];
const RESIZABLE_VERTICAL_LAYOUT_MAX_SIZES = [460, 600];

const TestDataButton = ({ onClick, disabled, isLoading }) => {
  return (
    <Tooltip
      title="Warning: This will send real data to your destination system and may cause changes. Use with caution."
      placement="top"
      arrow
    >
      <Button
        onClick={onClick}
        variant="outlined"
        size="small"
        color="basic"
        disabled={disabled || isLoading}
        className={styles.test_data_button}
      >
        {!disabled && isLoading && (
          <>
            Sending Data
            <CircularLoader
              size={22}
              thickness={1}
              color="var(--color-danger-600)"
              style={{ marginLeft: 'var(--spacing-md)' }}
            />
          </>
        )}
        {(disabled || !isLoading) && (
          <>
            Send Test Data
            <ArrowRight />
          </>
        )}
      </Button>
    </Tooltip>
  );
};

const TestTab = (props) => {
  const {
    description,
    error,
    isLoadingProbeSample,
    samples,
    editorLanguage,
    sinkConfig,
    contentType,
    panelHeading,
    testDataset,
    selectedCredentialId,
    isLoading,
    setFormError,
    accumulateSchema,
    accumulateSchemaToDestinationDraft,
    updateDestinationDraft,
    onToggleDestinationDraft,
    selectedConnection,
    addTemplateConfigForTestingSamples = false,
    parentDataSet,
    parentDataSetSamples,
  } = props;

  const [activePanelKey, setActivePanelKey] = React.useState(null);
  const [initialCode, setInitialCode] = React.useState(null);
  const [editedCode, setEditedCode] = React.useState(null);
  const [hasTestDataErrors, setTestDataErrors] = React.useState(false);
  const [isTestDataSuccess, setTestDataSuccess] = React.useState(false);
  const [testDataResponse, setTestDataResponse] = React.useState(null);
  const [testDataResponseMessage, setTestDataResponseMessage] = React.useState(null);

  const { paging } = usePaging();

  // apply dataset macro replacement to the sink config for api_server flows
  // const applyDatasetMacroReplacementLocal = (sinkConfig) => {
  //   return applyMacroReplacement(
  //     sinkConfig,
  //     parentDataSet,
  //     parentDataSetSamples
  //   );
  // };

  const resetTestDataStatus = () => {
    setTestDataErrors(false);
    setTestDataSuccess(false);
    setTestDataResponse(null);
    setTestDataResponseMessage(null);
  };

  const selectPanel = (panelKey) => {
    setEditedCode(null);
    setInitialCode(null);
    setActivePanelKey((prevState) => (panelKey !== prevState ? panelKey : null));
  };

  const handleChangeEditedCode = useDebouncedCallback(
    (value, event) => {
      setEditedCode(value);
    },
    [],
    300,
  );

  const handleSendTestData = async ({ event, nexlaMetaData }) => {
    event.stopPropagation();

    const sinkConfigById = paging.sinkConfigById;
    const sinkType = selectedConnection;
    const parseEditedCode = JSONUtils.parseJson(editedCode);

    if (!parseEditedCode) {
      setFormError(FORM_TYPE_EDIT_DESTINATION, 'Please enter a valid JSON');
      return;
    }

    // apply macro replacement to sinkConfig for api_server flows
    // Note: needs testing when parentDataSet is available for api server Target nodes
    const processedSinkConfig = applyMacroReplacement(
      sinkConfig,
      parentDataSet,
      parentDataSetSamples,
    );

    // set template_config for templatized API's
    let templatizedAPIPayload = null;
    if (addTemplateConfigForTestingSamples) {
      templatizedAPIPayload = UIUtils.buildSourceFromTemplatizedAPIConfig(
        processedSinkConfig,
        sinkConfigById[sinkType],
      );
    }
    const vendorEndpointId = processedSinkConfig.vendor_endpoint_id;

    const testUrl = processedSinkConfig?.['url.template'];

    if (!templatizedAPIPayload && !vendorEndpointId && (!testUrl || testUrl?.length === 0)) {
      setFormError(FORM_TYPE_EDIT_DESTINATION, 'Please enter a valid URL');
      return;
    }

    const testData = testDataset({
      id: selectedCredentialId,
      sampleData: parseEditedCode,
      nexlaMetaData,
      ...(templatizedAPIPayload
        ? {
            templateConfig: templatizedAPIPayload.template_config,
            vendorEndpointId,
            resourceType: 'SINK',
          }
        : { sinkConfig: processedSinkConfig }),
    });

    const schemaAccumulation = accumulateSchema(parseEditedCode);

    Promise.allSettled([testData, schemaAccumulation])
      .then((results) => {
        if (results[0].status === 'fulfilled' && results[1].status === 'fulfilled') {
          const [testDataResponse, schemaAccumulationResponse] = results;

          const testDataResponseStatusCodeOutput =
            testDataResponse?.value?.result?.output?.statusCode;
          const testDataResponseMessageOutput =
            testDataResponse?.value?.result?.output?.errorMessage;
          const testDataResponseOutput = testDataResponse?.value?.result?.output?.response;

          if (testDataResponseStatusCodeOutput !== 200) {
            setTestDataErrors(true);
            setTestDataSuccess(false);

            if (!testDataResponseMessageOutput) {
              setTestDataResponseMessage(
                `Error in sending test data! Status code: ${testDataResponseStatusCodeOutput}`,
              );
            } else {
              setTestDataResponseMessage(testDataResponseMessageOutput);
            }

            setTestDataResponse(testDataResponseOutput);

            return;
          }

          if (testDataResponseStatusCodeOutput === 200) {
            setTestDataResponseMessage(testDataResponseMessageOutput || 'Success');
            setTestDataResponse(testDataResponseOutput);
          }

          const samplesDataWithAttrs = [
            {
              attrs: { ...cloneDeep(parseEditedCode) },
              meta: [],
              error: null,
            },
          ];

          const rawSamplesData = [{ ...cloneDeep(parseEditedCode) }];

          updateDestinationDraft({
            samples: samplesDataWithAttrs,
            rawSamples: rawSamplesData,
          });
          accumulateSchemaToDestinationDraft(schemaAccumulationResponse.value);

          setTestDataErrors(false);
          setTestDataSuccess(true);

          if (isEqual(editedCode, initialCode)) {
            onToggleDestinationDraft(false);
          } else {
            onToggleDestinationDraft(true);
          }
        } else {
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              console.log(`Error in request ${index}:`, result.reason);
            }
          });
          setTestDataErrors(true);
          setTestDataSuccess(false);
          setTestDataResponseMessage(
            'Error in sending test data! Please check the payload and try again.',
          );
        }
      })
      .catch((error) => {
        console.log('Error in sending test data', error);
        setTestDataErrors(true);
        setTestDataSuccess(false);
        setTestDataResponseMessage(
          'Error in sending test data! Please check the payload and try again.',
        );
      });
  };

  let accordionHeaderMessageIcon;

  if (hasTestDataErrors) {
    accordionHeaderMessageIcon = <CloseFilledIcon />;
  } else if (isTestDataSuccess) {
    accordionHeaderMessageIcon = <CheckFilledIcon />;
  } else {
    accordionHeaderMessageIcon = <InfoIcon />;
  }

  let accordionResponseData;

  if (hasTestDataErrors) {
    accordionResponseData = (
      <div className={styles.full_height}>
        {Boolean(testDataResponse) && (
          <div className={cx(styles.accordion_header_response, styles.full_height)}>
            <LoadableMonacoEditor
              language={editorLanguage}
              value={testDataResponse}
              height="100%"
              theme="dark"
              options={{
                minimap: { enabled: false },
                readOnly: true,
              }}
            />
          </div>
        )}
      </div>
    );
  } else if (isTestDataSuccess) {
    accordionResponseData = (
      <div className={styles.full_height}>
        {Boolean(testDataResponse) && (
          <div className={cx(styles.accordion_header_response, styles.full_height)}>
            <div className={cx(styles.accordion_header_response, styles.full_height)}>
              <LoadableMonacoEditor
                language={editorLanguage}
                value={testDataResponse}
                height="100%"
                theme="dark"
                options={{
                  minimap: { enabled: false },
                  readOnly: true,
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  let content;

  if (error) {
    content = (
      <div className={styles.error_container}>
        <span
          style={{
            marginRight: 'var(--spacing-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'red',
          }}
        >
          {error}
        </span>
      </div>
    );
  } else if (isLoadingProbeSample) {
    content = 'Loading...';
  } else if (isEmpty(samples)) {
    content = 'No samples found.';
  } else {
    content = (
      <div id="request-payloads" className={styles.accordion_group_container}>
        <>
          {samples.map(({ nexlaMetaData, rawMessage }, index) => {
            const excludes = get(sinkConfig, 'mapping.excludes', []);
            const code = JSONUtils.parseToFormat(
              isPlainObject(rawMessage) ? omit(rawMessage, excludes) : rawMessage,
              contentType,
            );

            return (
              <Accordion
                key={index}
                square
                expanded={activePanelKey === index}
                onChange={() => {
                  selectPanel(index);
                  setEditedCode(code);
                  setInitialCode(code);
                  resetTestDataStatus();
                }}
              >
                <AccordionSummary className="accordion-title">
                  <div className={styles.accordion_title}>
                    <div className={styles.accordion_title_content}>
                      {activePanelKey === index ? (
                        <ArrowDropDown fontSize="large" />
                      ) : (
                        <ArrowRight fontSize="large" />
                      )}

                      <div className="accordion-content-grow">{`${panelHeading} ${index + 1}`}</div>
                    </div>

                    <TestDataButton
                      onClick={(event) =>
                        handleSendTestData({
                          event,
                          nexlaMetaData,
                        })
                      }
                      disabled={activePanelKey !== index}
                      isLoading={isLoading}
                    />
                  </div>
                </AccordionSummary>
                <AccordionDetails
                  className={cx(styles.accordion_body_container, 'accordion-body', 'full-width')}
                >
                  <ResizableVerticalLayout
                    initialSizes={[Boolean(testDataResponse) ? '150px' : '10%', '90%']}
                    minSizes={RESIZABLE_VERTICAL_LAYOUT_MIN_SIZES}
                    maxSizes={RESIZABLE_VERTICAL_LAYOUT_MAX_SIZES}
                    disableResize={!Boolean(testDataResponse)}
                  >
                    <div
                      className={cx(
                        {
                          [styles.accordion_header_error]: hasTestDataErrors,
                          [styles.accordion_header_success]: isTestDataSuccess,
                          [styles.accordion_header]: !hasTestDataErrors && !isTestDataSuccess,
                        },
                        styles.accordion_header_message,
                      )}
                    >
                      <div className={styles.accordion_header_icon_container}>
                        <div
                          className={cx(
                            styles.accordion_header_icon,
                            !testDataResponseMessage && styles.accordion_header_icon_no_message,
                          )}
                        >
                          {accordionHeaderMessageIcon}

                          <div className={styles.accordion_header_icon_message}>
                            {testDataResponseMessage ||
                              `Edit this JSON sample to customize your data. After making modifications, you can send a test payload to the destination or apply schema changes as needed.`}
                          </div>
                        </div>
                        {testDataResponse && (
                          <div className={styles.accordion_header_icon_message_response}>
                            SERVER RESPONSE:
                          </div>
                        )}
                      </div>
                      <div className={styles.full_height}>{accordionResponseData}</div>
                    </div>
                    <div className={styles.accordion_body_content}>
                      <LoadableMonacoEditor
                        language={editorLanguage}
                        value={code}
                        height={600}
                        theme="dark"
                        options={{
                          minimap: { enabled: false },
                        }}
                        onChange={handleChangeEditedCode}
                      />
                    </div>
                  </ResizableVerticalLayout>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
      </div>
    );
  }

  return (
    <>
      {description ? (
        <div className={cx(styles.description)}>
          <p>{description}</p>
        </div>
      ) : null}

      {content}
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { request, forms } = state;

  const selectedConnection =
    ownProps?.selectedConnection ?? forms[FORM_TYPE_EDIT_DESTINATION]?.data?.selectedConnection;

  return {
    isLoading: request.TEST_DATASET_REQUEST || request.ACCUMULATE_SCHEMA_REQUEST,
    selectedConnection,
  };
};

export default connect(mapStateToProps, {
  testDataset,
  accumulateSchema,
  accumulateSchemaToDestinationDraft,
  updateDestinationDraft,
  setFormError: setFormErrorConnect,
})(TestTab);
